import type { PayloadAction } from '@reduxjs/toolkit';

import type { AgentConversations, ChatMessage } from '~/common/model/api/chatbot';

import type { PopupState } from './_state.type';

/** Popup Reducer. */
export const popupReducer = {
  /**
   * Add message to chat.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<ChatMessage>} action Action.
   */
  addMessage: (state: PopupState, action: PayloadAction<ChatMessage>) => {
    state.messages.push(action.payload);
  },

  /**
   * Set session ID.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<string | null>} action Action.
   */
  setSessionId: (state: PopupState, action: PayloadAction<string | null>) => {
    state.sessionId = action.payload;
  },

  /**
   * Set agent conversations.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<AgentConversations | null>} action Action.
   */
  setConversations: (state: PopupState, action: PayloadAction<AgentConversations | null>) => {
    state.conversations = action.payload;
  },

  /**
   * Open chatbot popup.
   *
   * @param {PopupState} state State.
   */
  openPopup: (state: PopupState) => {
    state.isOpen = true;
  },

  /**
   * Close chatbot popup.
   *
   * @param {PopupState} state State.
   */
  closePopup: (state: PopupState) => {
    state.isOpen = false;
  },

  /**
   * Toggle chatbot popup.
   *
   * @param {PopupState} state State.
   */
  togglePopup: (state: PopupState) => {
    state.isOpen = !state.isOpen;
  },

  /**
   * Set pending message.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<string | null>} action Action.
   */
  setSummaryMessage: (state: PopupState, action: PayloadAction<string | null>) => {
    state.summaryMessage = action.payload;
  },

  /**
   * Set summary message and document ID.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<{ message: string | null; documentId: string | undefined }>} action Action.
   */
  setSummaryInfo: (state: PopupState, action: PayloadAction<{ message: string | null; documentId: string | undefined }>) => {
    state.summaryMessage = action.payload.message;
    state.summaryDocumentId = action.payload.documentId;
  },

  /**
   * Set summary message processing state.
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<boolean>} action Action.
   */
  setIsSummaryProcessing: (state: PopupState, action: PayloadAction<boolean>) => {
    state.isSummaryProcessing = action.payload;
  },

  /**
   * Set processing state (sending, data processing, or data source processing).
   *
   * @param {PopupState} state State.
   * @param {PayloadAction<boolean>} action Action.
   */
  setIsProcessing: (state: PopupState, action: PayloadAction<boolean>) => {
    state.isProcessing = action.payload;
  },
};
